import { useEffect, useState } from 'react';
import type { LotteryBlock, LotteryReportJob } from '@todo/shared';
import { lotteryViewApi } from '../../api/lottery.api';
import { ApiError } from '../../api/client';

type ReportBlock = Extract<LotteryBlock, { type: 'report' }>;

/**
 * A report the Lottery analyst started (Phase 8, the Phase 7 report jobs):
 * it polls the job until it is READY (then a Download button through the
 * short-lived link) or FAILED (the server's safe reason).
 */
export function ReportLinkCard({ block, pollMs = 3_000 }: { block: ReportBlock; pollMs?: number }) {
  const [job, setJob] = useState<LotteryReportJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const next = await lotteryViewApi.report(block.jobId);
        if (stopped) return;
        setJob(next);
        if (next.status === 'PENDING' || next.status === 'RUNNING') timer = setTimeout(() => void poll(), pollMs);
      } catch (e) {
        if (!stopped) setError(e instanceof ApiError ? e.message : 'The report status could not be read.');
      }
    };
    void poll();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [block.jobId, pollMs]);

  const download = async () => {
    try {
      const { url } = await lotteryViewApi.reportLink(block.jobId);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The download link could not be created.');
    }
  };

  const status = job?.status ?? 'PENDING';
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs" aria-live="polite">
      <div className="min-w-0">
        <p className="truncate font-semibold text-slate-800">{block.title}</p>
        <p className="text-slate-500">
          {block.format === 'pdf' ? 'PDF' : 'Excel'} report -{' '}
          {error ? <span className="text-red-700">{error}</span>
            : status === 'READY' ? 'ready'
              : status === 'FAILED' ? <span className="text-red-700">{job?.error ?? 'it could not be built'}</span>
                : 'being built…'}
        </p>
      </div>
      {status === 'READY' && !error && (
        <button type="button" onClick={() => void download()} className="shrink-0 rounded-md bg-blue-600 px-2.5 py-1 font-semibold text-white hover:bg-blue-700">
          Download {block.format === 'pdf' ? 'PDF' : 'Excel'}
        </button>
      )}
    </div>
  );
}
